import axios from 'axios';

// Use empty string fallback for SWA emulator mode (relative paths go through SWA proxy)
const API_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance using same base URL as other services
const adminApi = axios.create({
  baseURL: `${API_URL}/api/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get current authenticated user (same as authApiService)
const getCurrentUser = async () => {
  try {
    const response = await fetch('/.auth/me');
    if (response.ok) {
      const authData = await response.json();
      const userInfo = authData.clientPrincipal;
      if (userInfo) {
        return {
          id: userInfo.userId,
          email: userInfo.userDetails,
          name: userInfo.userClaims?.find(claim => claim.typ === 'name')?.val || userInfo.userDetails,
          provider: userInfo.identityProvider,
          roles: userInfo.userRoles
        };
      }
    }
  } catch (error) {
    console.error('Error fetching user info:', error);
  }
  return null;
};

// Add auth interceptor to include user credentials or demo token
adminApi.interceptors.request.use(
  async (config) => {
    // Check for demo token first
    const demoToken = sessionStorage.getItem('demoToken');
    if (demoToken) {
      config.headers['Authorization'] = `Bearer ${demoToken}`;
      return config;
    }

    // Otherwise, use OAuth principal email
    try {
      const user = await getCurrentUser();
      if (user && user.email) {
        config.headers['X-User-Email'] = user.email;
      }
    } catch (error) {
      console.error('Error getting auth data:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Global response interceptor for automatic logout on 401 (session timeout/token expiry)
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if demo session expired
      const demoSession = sessionStorage.getItem('demoSession');
      if (demoSession) {
        console.warn('Demo session expired (401). Redirecting to login...');
        sessionStorage.removeItem('demoToken');
        sessionStorage.removeItem('demoSession');
        window.location.href = '/';
      } else {
        console.warn('Session expired (401). Redirecting to login...');
        window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
      }
      return new Promise(() => {}); // Never resolves - page is redirecting
    }
    return Promise.reject(error);
  }
);

// Admin service functions
export const adminService = {
  // Check if current user has admin access
  async checkAccess() {
    try {
      const response = await adminApi.post('/check-access');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to check admin access');
    }
  },

  // Get list of all users
  async getUsers() {
    try {
      const response = await adminApi.get('/users');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch users');
    }
  },

  // Get detailed information for a specific user
  async getUserDetail(userId) {
    try {
      const response = await adminApi.get(`/user/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch user details');
    }
  },

  // Update user information
  async updateUser(userId, userData) {
    try {
      const response = await adminApi.put(`/user/${userId}`, userData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update user');
    }
  },

  // Delete user and all associated data
  async deleteUser(userId) {
    try {
      const response = await adminApi.delete(`/user/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to delete user');
    }
  },

  // Get system analytics
  async getAnalytics() {
    try {
      const response = await adminApi.get('/analytics');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch analytics');
    }
  },

  // Organization management functions
  async getOrganizations() {
    try {
      const response = await adminApi.get('/organizations');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch organizations');
    }
  },

  async createOrganization(orgData) {
    try {
      const response = await adminApi.post('/organizations', orgData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to create organization');
    }
  },

  async updateOrganization(orgId, orgData) {
    try {
      const response = await adminApi.put(`/organizations/${orgId}`, orgData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update organization');
    }
  },

  async deleteOrganization(orgId) {
    try {
      const response = await adminApi.delete(`/organizations/${orgId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to delete organization');
    }
  },

  async syncOrganizationUsers(orgId) {
    try {
      const response = await adminApi.post(`/organizations/${orgId}/sync-users`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to sync organization users');
    }
  },

  async getIndustries() {
    try {
      const response = await axios.get(`${API_URL}/api/config/industries`);
      return response.data?.industries || [];
    } catch (error) {
      console.error('Failed to load industries:', error);
      throw new Error(error.response?.data?.detail || 'Failed to load industries');
    }
  },

  async getLanguages() {
    try {
      const response = await axios.get(`${API_URL}/api/config/languages`);
      return response.data?.languages || [];
    } catch (error) {
      console.error('Failed to load languages:', error);
      throw new Error(error.response?.data?.detail || 'Failed to load languages');
    }
  }
};

export default adminService;
